import type { GeometryElement } from 'jsxgraph';
import type { JsxgraphThemeService } from '../../jsxgraph-theme.service';
import { pixelsToCoords } from '../label.creator';
import type { ElementCreatorContext } from '../core/board.types';
import type { LabelContext, ResolvedLabelConfig } from './label.model';
import { formatLabelText } from './label.formatters';
import { getAnchor, getCombinedOffset } from './label.position';
import { getLabelStyle } from './label.utils';

/**
 * Options for binding a label to an element.
 */
export interface BindLabelOptions {
  ctx: ElementCreatorContext;
  themeService: JsxgraphThemeService;
  element: GeometryElement;
  config: ResolvedLabelConfig;
  contextProvider: () => LabelContext;
}

/**
 * Extended label element with update capability.
 */
export interface BoundLabel extends GeometryElement {
  updateText(): void;
}

/**
 * Bind a label to an element with dynamic updates.
 *
 * @param options - Label binding options
 * @returns The created label element
 */
export function bindLabel(options: BindLabelOptions): BoundLabel {
  const { ctx, themeService, element, config, contextProvider } = options;
  const { board, container } = ctx;

  const theme = themeService.getTheme();
  const labelStyle = getLabelStyle(themeService, config.variant, config.color);
  const hasBackground = config.variant !== 'tertiary';
  const anchor = getAnchor(config.position);
  const combinedOffset = getCombinedOffset(config.position, config.offset);

  // Get position functions from the element
  const typedElement = element as unknown as { X: () => number; Y: () => number };

  // Create dynamic text function
  const getText = () => {
    const labelContext = contextProvider();
    return formatLabelText(config, labelContext);
  };

  // Create the label element
  const label = board.create(
    'text',
    [
      () => typedElement.X() + pixelsToCoords(board, container, combinedOffset[0], 'x'),
      () => typedElement.Y() + pixelsToCoords(board, container, combinedOffset[1], 'y'),
      () => `<span style="
        ${hasBackground ? `background-color: ${labelStyle.backgroundColor};` : ''}
        color: ${labelStyle.textColor};
        ${hasBackground ? `padding: ${theme.spacing.labelPadding};` : ''}
        ${hasBackground ? 'border-radius: 6px;' : ''}
        font-size: ${theme.typography.fontSize.label}px;
        font-weight: ${theme.typography.fontWeight.bold};
        font-family: ${theme.typography.fontFamily};
        white-space: nowrap;
        ${hasBackground ? 'display: inline-block;' : ''}
      ">${getText()}</span>`,
    ],
    {
      anchorX: anchor.anchorX,
      anchorY: anchor.anchorY,
      parse: false,
      useMathjax: false,
      useMathJax: false,
      cssClass: `jsx-${config.variant}-label-html`,
    },
  );

  return label as BoundLabel;
}

/**
 * Create a point label context provider.
 */
export function createPointLabelContext(name: string, point: GeometryElement): () => LabelContext {
  const typedPoint = point as unknown as { X: () => number; Y: () => number };
  return () => ({
    type: 'point' as const,
    name,
    x: () => typedPoint.X(),
    y: () => typedPoint.Y(),
  });
}

/**
 * Create an angle label context provider.
 */
export function createAngleLabelContext(name: string, angle: GeometryElement): () => LabelContext {
  const typedAngle = angle as unknown as { Value: () => number };
  return () => ({
    type: 'angle' as const,
    name,
    radians: () => typedAngle.Value(),
    degrees: () => (typedAngle.Value() * 180) / Math.PI,
  });
}

/**
 * Create a line label context provider.
 */
export function createLineLabelContext(name: string, line: GeometryElement): () => LabelContext {
  const typedLine = line as unknown as { L: () => number };
  return () => ({
    type: 'line' as const,
    name,
    length: () => typedLine.L(),
  });
}

/**
 * Create a polygon label context provider.
 */
export function createPolygonLabelContext(name: string, polygon: GeometryElement): () => LabelContext {
  const typedPolygon = polygon as unknown as { Area: () => number; Perimeter: () => number };
  return () => ({
    type: 'polygon' as const,
    name,
    area: () => typedPolygon.Area(),
    perimeter: () => typedPolygon.Perimeter(),
  });
}

/**
 * Create a curve label context provider.
 */
export function createCurveLabelContext(name: string): () => LabelContext {
  return () => ({
    type: 'curve' as const,
    name,
  });
}
